import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  EXAMPLE_GROUP_ID,
  OFFLINE_GC_TIME_MS,
  PlaceholderMemberService,
  queryKeys,
} from '@splitfiesta/core';
import type {
  CreateGroupDto,
  GroupMemberResponseDto,
  GroupResponseDto,
  GroupWithDashboard,
  PaginatedResponseDto,
  PlaceholderClaimDto,
  PlaceholderClaimListDto,
  PlaceholderClaimSummaryDto,
  UpdateGroupDto,
} from '@splitfiesta/shared';

import { checkIsOnline } from '@/hooks/useNetworkStatus';
import { useTypedApi } from '@/hooks/useTypedApi';

import {
  trackGroupCreated,
  trackGroupDeleted,
  trackGroupUpdated,
  trackMemberRemoved,
} from '@/lib/analytics';

import { useOfflineOwnerId, useOfflineStore } from '@/stores/offlineStore';

// staleTime: 0 means always refetch in background when online (keeps data fresh)
const OFFLINE_STALE_TIME = 0; // Always refetch when online to keep data current

interface GroupsParams {
  search?: string;
  category?: string;
  limit?: number;
}

// Fetch paginated groups list
export function useGroupsQuery(params?: GroupsParams) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.groupList(params),
    queryFn: async () => {
      const response = await api.groups.list({
        ...params,
        page: 1,
      });
      return response.data as PaginatedResponseDto<GroupResponseDto>;
    },
  });
}

// Fetch all groups with balances (no pagination)
// This is the primary hook for fetching user's groups with balance information
export function useGroupsWithBalancesQuery(
  params?: { search?: string },
  enabled = true
) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  // Normalize query key params - only include non-undefined values
  const queryKeyParams = params?.search ? { search: params.search } : undefined;

  return useQuery({
    queryKey: queryKeys.groupList(queryKeyParams),
    queryFn: async () => {
      const response = await api.groups.listWithBalances(params);

      // Backend returns array directly (no pagination wrapper)
      if (!response || !response.data) {
        return [] as GroupResponseDto[];
      }

      const groups = response.data as GroupResponseDto[];

      // Cache individual group data for offline expense creation
      // Transform memberBalances array to the format expected by ExpenseForm
      groups.forEach(group => {
        const groupWithMembers = {
          ...group,
          // Transform memberBalances to the 'members' format expected by ExpenseForm
          members: (group.memberBalances || []).map(m => ({
            userId: {
              _id: m.userId,
              name: m.userName,
              profileImage: m.profileImage,
            },
            ...(m.isPlaceholder && { isPlaceholder: true }),
            ...(m.claim && { claim: m.claim }),
          })),
          // Also keep balances for dashboard compatibility
          balances: group.memberBalances || [],
        };
        queryClient.setQueryData(queryKeys.group(group.id), groupWithMembers);
      });

      return groups;
    },
    enabled,
    staleTime: OFFLINE_STALE_TIME,
    gcTime: OFFLINE_GC_TIME_MS,
    refetchOnMount: 'always', // Always refetch on mount (PWA can't pull to refresh)
    networkMode: 'offlineFirst', // Use cached data when offline
    // Don't retry when offline - just use cached data
    retry: (failureCount, _error) => {
      // Don't retry if we're offline
      if (!checkIsOnline()) {
        return false;
      }
      // Otherwise, retry up to 3 times
      return failureCount < 3;
    },
    // Keep showing previous data while refetching fails
    placeholderData: previousData => previousData,
  });
}

/**
 * Extended GroupResponseDto with members field for ExpenseForm compatibility
 * ExpenseForm expects members in format: { userId: { _id, name, profileImage } }
 */
interface GroupWithMembers extends GroupResponseDto {
  members?: Array<{
    userId:
      | string
      | {
          _id: string;
          name?: string;
          profileImage?: string;
        };
    name?: string;
    profileImage?: string;
    isPlaceholder?: boolean;
    claim?: PlaceholderClaimSummaryDto;
  }>;
  balances?: GroupResponseDto['memberBalances'];
}

function memberIdOf(member: NonNullable<GroupWithMembers['members']>[number]) {
  return typeof member.userId === 'string' ? member.userId : member.userId._id;
}

/**
 * Target format for ExpenseForm members
 */
interface ExpenseFormMember {
  userId: {
    _id: string;
    name?: string;
    profileImage?: string;
  };
  isPlaceholder?: boolean;
  /** Drives the expense form's pending-claim hint and default participants. */
  claim?: PlaceholderClaimSummaryDto;
}

/**
 * Helper to transform group data to ensure members field exists in correct format
 * ExpenseForm requires group.members to be in format: { userId: { _id, name, profileImage } }
 *
 * Data sources and their formats:
 * 1. getById: returns `members` as flat { userId: string, name, profileImage }
 * 2. listWithBalances: returns `memberBalances` as { userId, userName, profileImage, balance }
 * 3. Cached data: may have `members` already in correct nested format
 */
function transformGroupWithMembers(
  group: GroupResponseDto
): GroupResponseDto & { members: ExpenseFormMember[] } {
  const groupWithMembers = group as GroupWithMembers;
  const transformedMembers: ExpenseFormMember[] = [];

  // Check if members already exist in the CORRECT nested format
  // The correct format has userId as an object with _id property
  if (groupWithMembers.members && groupWithMembers.members.length > 0) {
    const firstMember = groupWithMembers.members[0];
    const isNestedFormat =
      typeof firstMember.userId === 'object' && '_id' in firstMember.userId;

    if (isNestedFormat) {
      // Already in correct format
      return group as GroupResponseDto & { members: ExpenseFormMember[] };
    }

    // Members exist but in flat format (from getById endpoint)
    // Transform flat { userId: string, name, profileImage } to nested format
    for (const member of groupWithMembers.members) {
      transformedMembers.push({
        userId: {
          _id:
            typeof member.userId === 'string'
              ? member.userId
              : member.userId._id,
          name: member.name,
          profileImage: member.profileImage,
        },
        ...(member.isPlaceholder && { isPlaceholder: true }),
        ...(member.claim && { claim: member.claim }),
      });
    }
  }

  // If no members yet, try memberBalances (from listWithBalances endpoint)
  if (transformedMembers.length === 0 && group.memberBalances?.length) {
    for (const m of group.memberBalances) {
      transformedMembers.push({
        userId: {
          _id: m.userId,
          name: m.userName,
          profileImage: m.profileImage,
        },
        ...(m.isPlaceholder && { isPlaceholder: true }),
        ...(m.claim && { claim: m.claim }),
      });
    }
  }

  return {
    ...group,
    members: transformedMembers,
    balances: group.memberBalances || [],
  } as GroupResponseDto & { members: ExpenseFormMember[] };
}

// Fetch single group
export function useGroupQuery(groupId: string, enabled = true) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.group(groupId),
    queryFn: async () => {
      const response = await api.groups.getById(groupId);
      const group = response.data as GroupResponseDto;
      // Transform to ensure members field exists for ExpenseForm
      return transformGroupWithMembers(group);
    },
    enabled: enabled && !!groupId,
    // The claims list is owner/admin only, so the roster polls too: the
    // claimant's own row has to resolve.
    refetchInterval: query =>
      PlaceholderMemberService.isMergeInFlight(query.state.data?.members ?? [])
        ? PlaceholderMemberService.CLAIM_MERGE_POLL_MS
        : false,
    staleTime: OFFLINE_STALE_TIME,
    gcTime: OFFLINE_GC_TIME_MS,
    networkMode: 'offlineFirst', // Use cached data when offline
    // Don't retry when offline - just use cached data
    retry: (failureCount, _error) => {
      if (!checkIsOnline()) {
        return false;
      }
      return failureCount < 3;
    },
    placeholderData: previousData => previousData,
    // When offline, try to get initial data from cache
    // Entire function wrapped in try-catch to prevent any crashes
    initialData: () => {
      try {
        // FIRST: Check if this specific group is already cached
        // useGroupsWithBalancesQuery caches each group individually with queryKeys.group(id)
        const cachedGroup = queryClient.getQueryData<GroupResponseDto>(
          queryKeys.group(groupId)
        );
        if (cachedGroup) {
          // Ensure members field exists
          return transformGroupWithMembers(cachedGroup);
        }

        // SECOND: Search through groups list cache (handles both paginated and flat array)
        const allGroupsQueries = queryClient.getQueriesData<
          | { pages: PaginatedResponseDto<GroupResponseDto>[] }
          | GroupResponseDto[]
        >({
          queryKey: [...queryKeys.groups(), 'list'],
        });

        for (const [, data] of allGroupsQueries) {
          if (!data) continue;

          // Handle flat array (from useGroupsWithBalancesQuery)
          if (Array.isArray(data)) {
            const group = data.find(g => g.id === groupId);
            if (group) {
              return transformGroupWithMembers(group);
            }
          }
          // Handle paginated structure (from useGroupsQuery)
          else if (
            typeof data === 'object' &&
            data !== null &&
            'pages' in data &&
            Array.isArray(data.pages)
          ) {
            for (const page of data.pages) {
              if (page?.items) {
                const group = page.items.find(g => g.id === groupId);
                if (group) {
                  return transformGroupWithMembers(group);
                }
              }
            }
          }
        }

        // THIRD: Fallback to localStorage when offline and in-memory cache is empty
        // This handles page refresh while offline
        if (!checkIsOnline()) {
          const persistedCache = localStorage.getItem(
            'splitfiesta-query-cache'
          );
          if (persistedCache) {
            const parsed = JSON.parse(persistedCache);
            const queries = parsed.clientState?.queries;

            if (Array.isArray(queries)) {
              // First, check for direct group cache
              const directGroupQuery = queries.find(
                (q: { queryKey?: unknown[] }) =>
                  Array.isArray(q?.queryKey) &&
                  q.queryKey[0] === 'splitfiesta' &&
                  q.queryKey[1] === 'groups' &&
                  q.queryKey[2] === groupId
              );
              if (directGroupQuery?.state?.data) {
                return transformGroupWithMembers(
                  directGroupQuery.state.data as GroupResponseDto
                );
              }

              // Then, search groups list (handle both flat array and paginated)
              const groupsQuery = queries.find(
                (q: { queryKey?: unknown[] }) =>
                  Array.isArray(q?.queryKey) &&
                  q.queryKey[0] === 'splitfiesta' &&
                  q.queryKey[1] === 'groups' &&
                  q.queryKey[2] === 'list'
              );
              if (groupsQuery?.state?.data) {
                const listData = groupsQuery.state.data;

                // Handle flat array
                if (Array.isArray(listData)) {
                  const group = listData.find(
                    (g: GroupResponseDto) => g?.id === groupId
                  );
                  if (group) {
                    return transformGroupWithMembers(group);
                  }
                }
                // Handle paginated structure
                else if (
                  typeof listData === 'object' &&
                  listData !== null &&
                  Array.isArray(listData.pages)
                ) {
                  for (const page of listData.pages as PaginatedResponseDto<GroupResponseDto>[]) {
                    if (page?.items) {
                      const group = page.items.find(g => g?.id === groupId);
                      if (group) {
                        return transformGroupWithMembers(group);
                      }
                    }
                  }
                }
              }
            }
          }
        }
      } catch {
        // Silently fail - return undefined and let the query fetch normally
      }

      return undefined;
    },
  });
}

// Fetch group with dashboard data
export function useGroupWithDashboardQuery(
  groupId: string,
  enabled = true,
  options?: { refetchOnMount?: boolean | 'always' }
) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.groupWithDashboard(groupId),
    refetchOnMount: options?.refetchOnMount,
    queryFn: async () => {
      const response = await api.groups.getById(groupId, true);
      const data = response.data as GroupWithDashboard;

      // Also populate the basic group cache for offline support
      // This allows ExpenseForm to use cached data when navigating offline
      // Transform balances to members format for expense form compatibility
      const groupWithMembers = {
        ...data,
        // The form counts spots and shows the pending-claim hint off these.
        members: data.balances?.map(b => ({
          userId: {
            _id: b.userId,
            name: b.userName,
            profileImage: b.profileImage,
          },
          ...(b.isPlaceholder && { isPlaceholder: true }),
          ...(b.claim && { claim: b.claim }),
        })),
      };
      queryClient.setQueryData(queryKeys.group(groupId), groupWithMembers);

      return data;
    },
    enabled: enabled && !!groupId,
    refetchInterval: query =>
      PlaceholderMemberService.isMergeInFlight(query.state.data?.balances ?? [])
        ? PlaceholderMemberService.CLAIM_MERGE_POLL_MS
        : false,
    staleTime: OFFLINE_STALE_TIME,
    gcTime: OFFLINE_GC_TIME_MS,
    networkMode: 'offlineFirst', // Use cached data when offline
    // Don't retry when offline - just use cached data
    retry: (failureCount, _error) => {
      if (!checkIsOnline()) {
        return false;
      }
      return failureCount < 3;
    },
    placeholderData: previousData => previousData,
  });
}

// Create group mutation
export function useCreateGroupMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateGroupDto) => {
      const response = await api.groups.create(data);
      return response.data as GroupResponseDto;
    },
    onSuccess: data => {
      // Track analytics
      trackGroupCreated(data.id);
      // Invalidate groups list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.groups() });
    },
  });
}

// Update group mutation
export function useUpdateGroupMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateGroupDto) => {
      const response = await api.groups.update(groupId, data);
      return response.data as GroupResponseDto;
    },
    onSuccess: data => {
      // Track analytics
      trackGroupUpdated(groupId);
      // Update the specific group in cache immediately
      queryClient.setQueryData(queryKeys.group(groupId), data);
      // Mark queries as stale but DON'T refetch immediately (refetchType: 'none')
      // This prevents excessive network requests when saving - queries will refetch
      // naturally when the user navigates to a page that uses them
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupWithDashboard(groupId),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.groups(),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.activityFeeds(),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupActivityFeeds(groupId),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.activitiesByGroup(groupId),
        refetchType: 'none',
      });
    },
  });
}

// Delete group mutation
export function useDeleteGroupMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const ownerId = useOfflineOwnerId();
  const removePendingExpensesForGroup = useOfflineStore(
    state => state.removePendingExpensesForGroup
  );

  return useMutation({
    mutationFn: async (groupId: string) => {
      await api.groups.delete(groupId);
      return groupId;
    },
    onMutate: async (groupId: string) => {
      // Cancel any in-flight queries to prevent race conditions
      await queryClient.cancelQueries({ queryKey: queryKeys.groups() });

      // Optimistically remove the group from all list caches immediately
      // This prevents the deleted group from showing up even briefly
      queryClient.setQueriesData<{
        pages: PaginatedResponseDto<GroupResponseDto>[];
        pageParams: number[];
      }>({ queryKey: [...queryKeys.groups(), 'list'] }, oldData => {
        if (!oldData?.pages) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map(page => ({
            ...page,
            items: page.items?.filter(group => group.id !== groupId) ?? [],
            total: Math.max(0, (page.total ?? 0) - 1),
          })),
        };
      });
    },
    onSuccess: groupId => {
      removePendingExpensesForGroup(groupId, ownerId);
      // "Deleting" the example group is a device-side hide; keep it out of
      // the group_deleted metric.
      if (groupId !== EXAMPLE_GROUP_ID) trackGroupDeleted(groupId);

      // Remove ALL queries related to this group from cache
      // This prevents refetch attempts after deletion
      queryClient.removeQueries({ queryKey: queryKeys.group(groupId) });
      queryClient.removeQueries({
        queryKey: queryKeys.groupWithDashboard(groupId),
      });
      queryClient.removeQueries({ queryKey: queryKeys.groupBalances(groupId) });
      queryClient.removeQueries({
        queryKey: queryKeys.expensesByGroup(groupId),
      });
      queryClient.removeQueries({
        queryKey: queryKeys.groupReports.all(groupId),
      });
      queryClient.removeQueries({
        queryKey: queryKeys.activitiesByGroup(groupId),
      });
      queryClient.removeQueries({
        queryKey: queryKeys.settlements.group(groupId),
      });

      // Mark groups list as stale but DON'T refetch immediately
      // The optimistic update already removed the group from the UI
      // A background refetch will sync eventually when user interacts
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.groups(), 'list'],
        refetchType: 'none',
      });
    },
  });
}

// Remove member from group
export function useRemoveGroupMemberMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const ownerId = useOfflineOwnerId();
  const removePendingExpensesForGroup = useOfflineStore(
    state => state.removePendingExpensesForGroup
  );

  return useMutation({
    mutationFn: async (userId: string) => {
      await api.groups.removeMember(groupId, userId);
      return userId;
    },
    onSuccess: userId => {
      // Leaving: whatever is still queued for the group can never sync.
      if (userId === ownerId) {
        removePendingExpensesForGroup(groupId, ownerId);
      }
      // Track analytics
      trackMemberRemoved(groupId);
      // Invalidate group and members data
      queryClient.invalidateQueries({ queryKey: queryKeys.group(groupId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupMembers(groupId),
      });
    },
  });
}

// Update member role in group
export function useUpdateMemberRoleMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memberId,
      role,
    }: {
      memberId: string;
      role: 'admin' | 'member';
    }) => {
      await api.groups.updateMemberRole(groupId, memberId, role);
      return { memberId, role };
    },
    onSuccess: () => {
      // Invalidate group data to refresh UI (partial match will also invalidate groupWithDashboard)
      queryClient.invalidateQueries({ queryKey: queryKeys.group(groupId) });
    },
  });
}

// Every placeholder change can also resolve a claim, so both caches go stale
// together. `queryKeys.group` is a prefix of both the dashboard and the claims
// keys; the second call is explicit so the intent survives a key refactor.
function invalidatePlaceholderData(
  queryClient: ReturnType<typeof useQueryClient>,
  groupId: string
) {
  queryClient.invalidateQueries({ queryKey: queryKeys.group(groupId) });
  queryClient.invalidateQueries({
    queryKey: queryKeys.placeholderClaims(groupId),
  });
}

/** Polls while a merge can still finish by itself, under the roster's rule. */
export function claimsRefetchInterval(
  claims: PlaceholderClaimDto[] | undefined
): number | false {
  return claims &&
    PlaceholderMemberService.isMergeInFlight(claims.map(claim => ({ claim })))
    ? PlaceholderMemberService.CLAIM_MERGE_POLL_MS
    : false;
}

// Claims on this group's placeholders (owner-only endpoint, so callers pass
// `enabled: isOwner`)
export function useGroupClaimsQuery(
  groupId: string,
  options?: { enabled?: boolean; refetchOnMount?: boolean | 'always' }
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.placeholderClaims(groupId),
    refetchOnMount: options?.refetchOnMount,
    queryFn: async () => {
      const response = await api.groups.listPlaceholderClaims(groupId);
      const data = response.data as PlaceholderClaimListDto;
      return data?.claims ?? [];
    },
    enabled: (options?.enabled ?? false) && !!groupId,
    refetchInterval: query => claimsRefetchInterval(query.state.data),
    staleTime: OFFLINE_STALE_TIME,
    gcTime: OFFLINE_GC_TIME_MS,
    networkMode: 'offlineFirst',
    retry: (failureCount, _error) => {
      if (!checkIsOnline()) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * The group rides in the variables: the expense form's group can change while
 * the request runs, and the cache write must land in the group the spot was
 * made in.
 */
export interface CreatePlaceholderVariables {
  groupId: string;
  name: string;
}

// Add a member without an account (any member)
export function useCreatePlaceholderMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, name }: CreatePlaceholderVariables) => {
      const response = await api.groups.createPlaceholder(groupId, name);
      return response.data as GroupMemberResponseDto;
    },
    // Written before the refetch so the expense form can place the new spot
    // at once; the invalidation then settles both caches.
    onSuccess: (member, { groupId }) => {
      queryClient.setQueryData<GroupWithMembers>(
        queryKeys.group(groupId),
        prev =>
          prev?.members &&
          !prev.members.some(m => memberIdOf(m) === member.userId)
            ? {
                ...prev,
                members: [
                  ...prev.members,
                  {
                    userId: {
                      _id: member.userId,
                      name: member.name,
                      profileImage: member.profileImage,
                    },
                    isPlaceholder: true,
                  },
                ],
              }
            : prev
      );
      queryClient.setQueryData<GroupWithDashboard>(
        queryKeys.groupWithDashboard(groupId),
        prev =>
          prev?.balances && !prev.balances.some(b => b.userId === member.userId)
            ? {
                ...prev,
                balances: [
                  ...prev.balances,
                  {
                    userId: member.userId,
                    userName: member.name,
                    balance: 0,
                    currency: prev.currency,
                    role: member.role,
                    joinedAt: member.joinedAt,
                    isPlaceholder: true,
                    ...(member.createdBy && { createdBy: member.createdBy }),
                  },
                ],
              }
            : prev
      );
      invalidatePlaceholderData(queryClient, groupId);
    },
  });
}

// Rename a placeholder member (owner/admin)
export function useUpdatePlaceholderMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, name }: { userId: string; name: string }) => {
      const response = await api.groups.updatePlaceholder(
        groupId,
        userId,
        name
      );
      return response.data as GroupMemberResponseDto;
    },
    onSuccess: () => {
      invalidatePlaceholderData(queryClient, groupId);
    },
  });
}

// Remove a placeholder member (owner/admin)
export function useDeletePlaceholderMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await api.groups.deletePlaceholder(groupId, userId);
      return userId;
    },
    onSuccess: () => {
      invalidatePlaceholderData(queryClient, groupId);
    },
  });
}

// Say a placeholder is you, from inside the group (any member, one live
// claim per group — the API refuses a second one)
export function useRequestPlaceholderClaimMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const response = await api.groups.requestPlaceholderClaim(
        groupId,
        userId
      );
      return response.data as PlaceholderClaimDto;
    },
    // onSettled, not onSuccess: a refusal means the roster this page drew the
    // pill from is stale (someone else claimed that placeholder), so the list
    // has to refetch for the row to correct itself.
    onSettled: () => {
      invalidatePlaceholderData(queryClient, groupId);
    },
  });
}

// Accept a claim and merge the claimant into the placeholder (owner only)
export function useConfirmPlaceholderClaimMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      claimantId,
    }: {
      userId: string;
      claimantId: string;
    }) => {
      const response = await api.groups.confirmPlaceholderClaim(
        groupId,
        userId,
        claimantId
      );
      return response.data as PlaceholderClaimDto;
    },
    onSuccess: () => {
      invalidatePlaceholderData(queryClient, groupId);
    },
  });
}

// Alias for useGroupsWithBalancesQuery for backwards compatibility
// Components that used useAllGroupsWithBalancesQuery can continue to work
export function useAllGroupsWithBalancesQuery(enabled = true) {
  const { data, isLoading, error } = useGroupsWithBalancesQuery(
    undefined,
    enabled
  );

  return {
    data: data ?? [],
    isLoading,
    error,
  };
}
